/**
 * Configuration 페이지 공용 컨트롤러 훅
 * LITE/MINI 페이지가 공유하는 저장/로드/스토리지 핸들러와 모달 상태를 한곳에 모은다.
 * 기기 종류별로 달라지는 부분(저장 가능 조건, 전송 전 변환, 저장 후 데이터 적용)만 주입받는다.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useAppStore } from '@/store/loadingStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useModalStore } from '@/store/modalStore';
import {
  getSavedConfigurations,
  hasConfigurationWithName,
  saveConfiguration,
} from '@/utils/configStorage';
import { buildStorageConfigData, isConnectionLostError } from '../utils/configFileHelpers';

interface SaveResult {
  success: boolean;
  error?: string;
}

interface RefreshResult {
  success: boolean;
  data?: { deviceInfo: unknown; deviceConfig: unknown };
}

interface UseConfigPageControllerParams {
  deviceType: DeviceType;
  curDevice: unknown;
  deviceConfigData: Record<string, unknown>;
  /** 현재 설정에 검증 에러가 있는지 */
  hasErrors: () => boolean;
  /** 기기에 저장 가능한 상태인지 (LITE: 시리얼 일치까지, MINI: 연결+호환) */
  canSaveToDevice: boolean;
  saveDeviceConfig: (config: any, device: any) => Promise<SaveResult>;
  refreshDeviceInfo: () => Promise<RefreshResult>;
  /** 전송 직전 config 변환 (LITE: 그대로, MINI: 배열->단일값) */
  buildConfigToSend: (data: Record<string, unknown>) => any;
  /** 저장 후 refresh된 기기 정보/설정을 페이지 상태에 반영 */
  onSaved: (deviceInfo: unknown, deviceConfig: unknown) => void;
}

export function useConfigPageController(params: UseConfigPageControllerParams) {
  const {
    deviceType,
    curDevice,
    deviceConfigData,
    hasErrors,
    canSaveToDevice,
    saveDeviceConfig,
    refreshDeviceInfo,
    buildConfigToSend,
    onSaved,
  } = params;

  const { t } = useTranslation('config');
  const { showLoading, hideLoading } = useAppStore();
  const { showSuccess, showError } = useNotificationStore();
  const { showConfirm } = useModalStore();

  const [showConfigListModal, setShowConfigListModal] = useState(false);
  const [showNameInputModal, setShowNameInputModal] = useState(false);

  // ===== localStorage 저장 =====

  const handleSaveToStorage = useCallback(() => {
    if (hasErrors()) {
      showError(i18nKeys.config.msg_fileSaveFailed, i18nKeys.config.msg_settingsHasErrors);
      return;
    }
    setShowNameInputModal(true);
  }, [hasErrors, showError]);

  const handleSaveWithName = useCallback(
    (name: string) => {
      try {
        const configData = buildStorageConfigData(curDevice, deviceConfigData, deviceType);

        if (hasConfigurationWithName(name, deviceType)) {
          setShowNameInputModal(false);
          showConfirm(
            t(i18nKeys.config.msg_configNameExists || 'msg_configNameExists'),
            t(i18nKeys.config.msg_configNameExistsDesc || 'msg_configNameExistsDesc', { name }),
            () => {
              saveConfiguration(configData, name, deviceType);
              showSuccess(i18nKeys.config.msg_configSaved, i18nKeys.config.msg_configSaved);
            },
            () => {
              setTimeout(() => setShowNameInputModal(true), 100);
            },
            'primary',
            t(i18nKeys.config.overwrite || 'overwrite'),
            t(i18nKeys.config.cancel)
          );
          return;
        }

        setShowNameInputModal(false);
        saveConfiguration(configData, name, deviceType);
        showSuccess(i18nKeys.config.msg_configSaved, i18nKeys.config.msg_configSaved);
      } catch {
        showError(i18nKeys.config.msg_configSaveFailed, i18nKeys.config.msg_configSaveFailed);
      }
    },
    [curDevice, deviceConfigData, deviceType, showConfirm, showSuccess, showError, t]
  );

  // ===== localStorage 로드 =====

  const handleLoadFromStorage = useCallback(() => {
    const savedConfigs = getSavedConfigurations(deviceType);
    if (savedConfigs.length === 0) {
      showError(
        i18nKeys.config.msg_noSavedConfigurations,
        i18nKeys.config.msg_noSavedConfigurationsDesc
      );
      return;
    }
    setShowConfigListModal(true);
  }, [deviceType, showError]);

  // ===== 기기에 설정 저장 =====

  const handleSave = useCallback(async () => {
    if (!canSaveToDevice) {
      showError(i18nKeys.config.msg_settingsSaveFailed, i18nKeys.config.msg_settingsDataMismatch);
      return;
    }

    if (hasErrors()) {
      showError(i18nKeys.config.msg_settingsSaveFailed, i18nKeys.config.msg_settingsHasErrors);
      return;
    }

    // 연결 끊김이면 전용 메시지, 아니면 fallback 메시지를 표시 (else/catch 공용)
    const notifySaveError = (errorMsg: string, fallbackMessage: string) => {
      if (isConnectionLostError(errorMsg)) {
        showError(i18nKeys.config.msg_settingsSaveFailed, t(i18nKeys.config.msg_connectionLost));
      } else {
        showError(i18nKeys.config.msg_settingsSaveFailed, fallbackMessage);
      }
    };

    try {
      showConfirm(
        t(i18nKeys.config.saveSettings),
        t(i18nKeys.config.msg_confirmSaveSettings),
        async () => {
          try {
            showLoading(t(i18nKeys.config.msg_settingsSaving));

            const result = await saveDeviceConfig(buildConfigToSend(deviceConfigData), curDevice);

            if (result.success) {
              showSuccess(
                i18nKeys.config.msg_settingsSaveSuccess,
                i18nKeys.config.msg_settingsSavedSuccessfully
              );
              try {
                const refreshResult = await refreshDeviceInfo();
                if (refreshResult.success && refreshResult.data) {
                  onSaved(refreshResult.data.deviceInfo, refreshResult.data.deviceConfig);
                }
              } catch {
                // refresh 실패는 치명적이지 않음
              }
            } else {
              notifySaveError(
                result.error || i18nKeys.config.msg_settingsSavedFailed,
                i18nKeys.config.msg_settingsSavedFailed
              );
            }
          } catch (error) {
            const errorMsg =
              error instanceof Error ? error.message : i18nKeys.config.msg_unknownError;
            notifySaveError(errorMsg, errorMsg);
          } finally {
            hideLoading();
          }
        },
        () => {},
        'primary'
      );
    } catch (error) {
      showError(
        i18nKeys.config.msg_settingsSaveFailed,
        error instanceof Error ? error.message : t(i18nKeys.config.msg_unknownError)
      );
    }
  }, [
    canSaveToDevice,
    hasErrors,
    showError,
    showConfirm,
    showLoading,
    hideLoading,
    showSuccess,
    t,
    saveDeviceConfig,
    buildConfigToSend,
    deviceConfigData,
    curDevice,
    refreshDeviceInfo,
    onSaved,
  ]);

  return {
    showConfigListModal,
    setShowConfigListModal,
    showNameInputModal,
    setShowNameInputModal,
    handleSaveToStorage,
    handleSaveWithName,
    handleLoadFromStorage,
    handleSave,
  };
}
